import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Button, Stack, Alert, CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '../../hooks/useNotifications';
import billingService from '../../services/billingService';

const COUNTDOWN_SECONDS = 10 * 60; // 10 min (D-2)
const POLL_INTERVAL_MS = 20000;    // fallback lento se o SignalR cair
// Mesmos desfechos de sucesso da BillingReturnPage (fulfillment idempotente).
const SUCCESS = new Set(['Applied', 'AlreadyFulfilled', 'Renewed', 'AlreadyApplied']);

function formatMMSS(total) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * PixQrCheckout — mostra o QR Code PIX e confirma o pagamento SEM sair da tela (D-2).
 *
 * Primário: SignalR (push `billing.order.paid` → confirma o NOSSO pedido via callbackToken,
 * que é idempotente no backend). Fallback: poll lento (~20s). Countdown de 10 min.
 *
 * ⚠️ Montar APENAS enquanto há uma order ativa (o SignalR vive só durante o checkout). O pai
 * deve passar `onClose`/`onSuccess` ESTÁVEIS (useCallback) — senão o hook reconecta a cada render.
 *
 * @param {{ order: { pix?: {encodedImage:string, payload:string, expiresAtUtc?:string}, callbackToken?: string }, onClose: Function, onSuccess?: Function }} props
 */
export default function PixQrCheckout({ order, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('waiting'); // waiting | success | expired
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [copied, setCopied] = useState(false);
  const settledRef = useRef(false); // trava: não confirma/duplica após desfecho

  const pix = order?.pix;
  const callbackToken = order?.callbackToken;

  const finishSuccess = useCallback((result) => {
    if (settledRef.current) return;
    settledRef.current = true;
    setStatus('success');
    queryClient.invalidateQueries({ queryKey: ['entitlements'] }); // saldo reflete na hora
    onSuccess?.(result);
  }, [queryClient, onSuccess]);

  // Confirma o pagamento (idempotente no backend). Chamado pelo push E pelo poll.
  const tryConfirm = useCallback(async () => {
    if (settledRef.current || !callbackToken) return;
    try {
      const r = await billingService.confirmOrder(callbackToken);
      if (SUCCESS.has(r.fulfillmentOutcome) || SUCCESS.has(r.billingOutcome)) {
        finishSuccess(r);
      }
    } catch {
      // silencioso — o poll/SignalR tenta de novo
    }
  }, [callbackToken, finishSuccess]);

  // Primário: SignalR. Ao receber `billing.order.paid`, confirma (gatilho — o push não traz orderId).
  const handleNotification = useCallback((notification) => {
    const type = notification?.type ?? notification?.notificationType;
    if (type === 'billing.order.paid') tryConfirm();
  }, [tryConfirm]);
  useNotifications(handleNotification);

  // Fallback: poll lento enquanto aguardando.
  useEffect(() => {
    if (status !== 'waiting') return undefined;
    const id = setInterval(tryConfirm, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status, tryConfirm]);

  // Countdown de 10 min (1 tick/s). Ao zerar, marca expirado (se ainda não houve sucesso).
  useEffect(() => {
    if (status !== 'waiting') return undefined;
    if (secondsLeft <= 0) {
      if (!settledRef.current) setStatus('expired');
      return undefined;
    }
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [status, secondsLeft]);

  const handleCopy = useCallback(async () => {
    if (!pix?.payload) return;
    try {
      await navigator.clipboard.writeText(pix.payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [pix]);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Pagamento via PIX</DialogTitle>
      <DialogContent>
        {status === 'success' ? (
          <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6">Pagamento confirmado! 🎉</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Seus créditos já foram liberados.
            </Typography>
            <Button variant="contained" onClick={onClose}>Fechar</Button>
          </Stack>
        ) : status === 'expired' ? (
          <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
            <AccessTimeIcon color="warning" sx={{ fontSize: 56 }} />
            <Typography variant="h6">QR Code expirado</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Se você já pagou, aguarde alguns instantes — o sistema reconhece automaticamente.
              Caso contrário, gere um novo pagamento.
            </Typography>
            <Button onClick={onClose}>Fechar</Button>
          </Stack>
        ) : !pix?.encodedImage ? (
          <Box sx={{ py: 3 }}>
            <Alert severity="error">Não foi possível gerar o QR Code. Tente novamente.</Alert>
          </Box>
        ) : (
          <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
            <Box
              component="img"
              src={`data:image/png;base64,${pix.encodedImage}`}
              alt="QR Code PIX"
              sx={{ width: 240, height: 240, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            />
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy} fullWidth>
              {copied ? 'Copiado!' : 'Copiar código (copia e cola)'}
            </Button>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Expira em <b>{formatMMSS(secondsLeft)}</b>
              </Typography>
            </Stack>
            <Alert severity="info" sx={{ width: '100%' }}>
              Após o pagamento, aguarde o reconhecimento pelo sistema — esta tela atualiza sozinha.
            </Alert>
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Aguardando confirmação…</Typography>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
